<!--
+------------------------------------------------------------------+
| Author: Jeffrey Thümann                                          |
| Component: StoryCard                                             |
| Section: components                                              |
| Purpose: Displays Story details in a card layout.                |
+------------------------------------------------------------------+
-->
<template>
    <div :class="['StoryCard wrapper', { 'StoryCard--reverse': reverse }]" ref="storyCard">
        <component :is="textContainerTag"
            :class="['text_container', { 'text_container--reverse': reverse, 'text_container--clickable': clickableTextContainer }]"
            :href="clickableTextContainer ? buttonHref : null" :aria-label="textContainerAriaLabel" ref="textContainer">
            <div class="text_wrapper">
                <TitleGroup v-if="titleGroupTitle" :title="titleGroupTitle" />
                <p class="story_summary" v-if="description">{{ description }}</p>
                <h2 v-if="contactForm">{{ $t('contactPage.storyCard.heading') }}</h2>
                <p v-if="contactForm">{{ $t('contactPage.storyCard.text') }}</p>
                <ContactForm v-if="contactForm" />
                <slot></slot>
            </div>
            <span v-if="clickableTextContainer" class="BtnPrimary text_container_cta" aria-hidden="true">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="17" viewBox="0 0 16 17" fill="#FFFFFF">
                    <path
                        d="M15.7063 9.20627C16.0969 8.81565 16.0969 8.18127 15.7063 7.79065L11.7063 3.79065C11.3156 3.40002 10.6812 3.40002 10.2906 3.79065C9.9 4.18127 9.9 4.81565 10.2906 5.20627L12.5844 7.50002H1C0.446875 7.50002 0 7.9469 0 8.50002C0 9.05315 0.446875 9.50002 1 9.50002H12.5844L10.2906 11.7938C9.9 12.1844 9.9 12.8188 10.2906 13.2094C10.6812 13.6 11.3156 13.6 11.7063 13.2094L15.7063 9.2094V9.20627Z"
                        fill="#FFFFFF" />
                </svg>
                {{ buttonLabel }}
            </span>
        </component>
        <div class="photo_container" ref="photoContainer" :style="{
            backgroundImage: imageBackgroundImage,
            backgroundSize: cover ? 'cover' : 'contain',
            backgroundColor: transparentPhotoBackground ? 'transparent' : 'var(--brand-bg-5)'
        }">
        </div>
    </div>
</template>

<script>
import TitleGroup from '@/atoms/TitleGroup.vue';
import ContactForm from "@/components/ContactForm.vue";
import { handleScroll } from '@/utils/scrollUtils';
import { debugLog } from '@/utils/logger';
import { getBackgroundImageUrl } from '@/utils/mediaUtils';

export default {
    name: 'StoryCard',
    components: {
        TitleGroup,
        ContactForm
    },
    props: {
        description: {
            type: String,
            required: true
        },
        imageSrc: {
            type: String,
            required: true
        },
        reverse: {
            type: Boolean,
            default: false
        },
        titleGroupTitle: {
            type: String,
            required: true
        },
        buttonLabel: {
            type: String,
            required: true
        },
        buttonHref: {
            type: String,
            required: true
        },
        contactForm: {
            type: Boolean,
            default: false
        },
        cover: {
            type: Boolean,
            default: false
        },
        transparentPhotoBackground: {
            type: Boolean,
            default: false
        },
        summary: {
            type: String,
            required: false
        },
    },
    mounted() {
        this.handleScroll = () => handleScroll({
            textContainer: this.$refs.textContainer,
            element: this.$refs.storyCard // or BioDataCard for BioDataCard component
        });

        // Call handleScroll on mount to set the correct position immediately
        this.handleScroll();

        // Add the scroll event listener
        window.addEventListener('scroll', this.handleScroll);

        // Call setDynamicPadding and add a resize event listener
        this.setDynamicPadding();
        window.addEventListener('resize', this.setDynamicPadding);
    },
    beforeUnmount() {
        // Remove the scroll and resize event listeners
        window.removeEventListener('scroll', this.handleScroll);
        window.removeEventListener('resize', this.setDynamicPadding);
    },
    watch: {
        description() {
            debugLog('description changed');
            this.setDynamicPadding();
        },
        reverse() {
            debugLog('reverse changed');
            this.setDynamicPadding();
        }
    },
    computed: {
        imageBackgroundImage() {
            return getBackgroundImageUrl(this.imageSrc);
        },
        clickableTextContainer() {
            return Boolean(this.buttonLabel && this.buttonHref);
        },
        textContainerTag() {
            return this.clickableTextContainer ? 'a' : 'div';
        },
        textContainerAriaLabel() {
            if (!this.clickableTextContainer) {
                return null;
            }

            return this.titleGroupTitle
                ? `${this.buttonLabel}: ${this.titleGroupTitle}`
                : this.buttonLabel;
        },
    },

    methods: {
        handleScroll() {
            const textContainer = this.$refs.textContainer;
            const storyCard = this.$refs.storyCard;

            if (textContainer && storyCard) {
                const storyCardRect = storyCard.getBoundingClientRect();
                const scrollPosition = window.scrollY || window.pageYOffset;
                const elementTop = storyCardRect.top + scrollPosition;
                const windowHeight = window.innerHeight;

                const maxTop = 192; // Max top value
                const minTop = -96;   // Min top value

                // Calculate how much of the card is in view
                const scrollFraction = (scrollPosition + windowHeight - elementTop) / (windowHeight + storyCard.offsetHeight);

                // Clamp the scrollFraction between 0 and 1
                const clampedScrollFraction = Math.max(0, Math.min(1, scrollFraction));

                // Apply a gentler easing function to make the movement slower in the middle
                const easedScrollFraction = this.gentleEaseOutIn(clampedScrollFraction);

                // Calculate the new top value using the eased fraction
                const newTop = maxTop - (maxTop - minTop) * easedScrollFraction;

                // Apply the new top value immediately when page loads or on scroll
                textContainer.style.top = `${newTop}px`;
            }
        },

        // A gentler easing function to slow down the effect
        gentleEaseOutIn(t) {
            // This creates a more gentle easing effect
            return t < 0.5 ? 0.5 * (1 - Math.pow(1 - 2 * t, 3)) : 0.5 * (Math.pow(2 * t - 1, 3) + 1);
        },
        setDynamicPadding() {
            const textContainer = this.$refs.textContainer;
            const storyCard = this.$refs.storyCard;
            const photoContainer = this.$refs.photoContainer; // Ensure this ref is added to photo_container in the template

            if (textContainer && storyCard && photoContainer) {
                const textContainerHeight = textContainer.offsetHeight;
                const photoContainerHeight = photoContainer.offsetHeight; // Dynamically fetch the height of photo_container
                const dynamicThreshold = Math.max(0, photoContainerHeight - 96); // Calculate the threshold dynamically

                // Calculate padding-bottom based on the dynamic threshold
                let paddingBottom = Math.max(0, textContainerHeight - dynamicThreshold);

                storyCard.style.paddingBottom = `${paddingBottom}px`;

                debugLog({
                    textContainerHeight,
                    photoContainerHeight,
                    dynamicThreshold,
                    paddingBottom,
                }); // Useful for debugging
            }
        }
    }
}
</script>

<style scoped>
.StoryCard {
    position: relative;
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
}

.StoryCard--reverse {
    justify-content: flex-start;
}

.text_container {
    display: flex;
    width: 100%;
    max-width: calc(50% + 48px);
    /* padding: 48px; */
    flex-direction: column;
    align-items: flex-start;
    background: var(--brand-bg-1);
    /* box-shadow: var(--shadow-card-lg); */
    position: absolute;
    left: 0;
    top: 96px;
}

.text_container--reverse {
    right: 0;
    left: auto;
}

.text_container--clickable {
    cursor: pointer;
}

.text_container--clickable:hover .text_container_cta {
    box-shadow: 0px 0px 0px 4px var(--brand-color-1);
    text-decoration: underline;
}

.text_wrapper {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 24px;
    align-self: stretch;
}

.photo_container {
    background-position: center;
    background-size: contain;
    background-repeat: no-repeat;
    width: 100%;
    max-width: calc(50% + 48px);
    height: 500px;
    border-radius: var(--border-radius-sm);
}

.story_summary {
    font-size: 18px;
    line-height: 165%;
}

@media (max-width: 1320px) {
    .photo_container {
        height: auto;
        padding-bottom: 39.3%;
    }
}

@media (max-width: 1080px) {
    .text_container {
        padding: 48px;
    }
}

@media (max-width: 860px) {
    .StoryCard {
        flex-direction: column-reverse;
        padding-bottom: 0 !important;
    }

    .text_container {
        position: static;
        max-width: 100%;
        margin: -48px auto 0 auto;
        gap: 20px;
        padding: 24px;
        z-index: 2;
    }

    .text_wrapper {
        gap: 16px;
    }

    .photo_container {
        padding-bottom: 81%;
        max-width: calc(100% + 40px);
        width: calc(100% + 40px);
        margin-left: -20px;
        z-index: 1;
    }
}
</style>
